import {
    Children,
    useRef,
    useState,
    useMemo,
    useCallback,
    useEffect,
    ReactElement,
    ReactNode,
    FunctionComponent,
} from "react"
import classNames from "classnames"

interface TabProps {
    title: string
    id: string
    children: ReactNode
}
const Tab: FunctionComponent<TabProps> = () => null

type TabChild = ReactElement<TabProps> | boolean

function getTabData(children: TabChild | TabChild[]) {
    const data: { title: string; id: string; content: ReactNode }[] = []
    Children.map<void, TabChild>(children, (el: TabChild) => {
        if (el && typeof el === "object") {
            data.push({
                title: el.props.title,
                id: el.props.id,
                content: el.props.children,
            })
        }
    })
    return data
}

// hacky solution to overflow parent border

const borderStyle = {
    marginTop: "-1px",
}

const tabBorderStyle = {
    height: "4px",
}

interface TabsProps {
    onChangeTab(currentId: string): void
    children: TabChild | TabChild[]
}

export function Tabs({ onChangeTab, children }: TabsProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const items = useMemo(() => getTabData(children), [])
    const [currentId, setCurrentId] = useState(items.length > 0 ? items[0].id : null)
    const [overflowX, setOverflowX] = useState(false)

    const currentItem = items.find((item) => item.id === currentId)

    const selectTab = useCallback(
        (id: string) => {
            setCurrentId(id)
        },
        [currentId]
    )

    useEffect(() => {
        function listenerScroll() {
            const element = containerRef.current
            if (element) {
                setOverflowX(!(element.offsetWidth + element.scrollLeft >= element.scrollWidth))
            }
        }

        if (containerRef.current) {
            const element = containerRef.current

            listenerScroll()
            containerRef.current.addEventListener("scroll", listenerScroll)

            return () => {
                element.removeEventListener("scroll", listenerScroll)
            }
        }
    }, [])

    useEffect(() => {
        currentId && onChangeTab(currentId)
    }, [currentId])

    return (
        <div className="d-flex flex-column">
            <div
                className={classNames({
                    "overflow-gradient": overflowX,
                })}
            >
                <div className="d-flex font-size-5 hide-scrollbar-x" ref={containerRef}>
                    {items.map((item) => {
                        const isSelected = currentId === item.id
                        const color = isSelected ? "color-green-2" : "color-blue-3"
                        const hover = isSelected ? "" : "hover-green-2"
                        return (
                            <div className="tabs--item" key={item.id}>
                                <div
                                    role="button"
                                    className={`${color} ${hover} pb-3 cursor-pointer font-weight-1 text-nowrap`}
                                    onClick={() => selectTab(item.id)}
                                >
                                    {item.title}
                                </div>
                                <div
                                    style={tabBorderStyle}
                                    className={`border-radius-top-5 ${
                                        isSelected ? "bg-green-2" : "bg-transparent"
                                    }`}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="bb-blue-1 mb-5" style={borderStyle} />

            {items.map((item) => (
                <div key={item.id} className={item === currentItem ? "d-block" : "d-none"}>
                    {item.content}
                </div>
            ))}
        </div>
    )
}

Tabs.Tab = Tab
